const MAX_SIDE = 1000;
const PADDING = 8;
const FEATHER = 40;
const GRID_DIVISIONS = 32;
const MIN_CELL = 8;

export const DEFAULT_SENSITIVITY = 40;
export const SENSITIVITY_MIN = 0;
export const SENSITIVITY_MAX = 120;

export type InkColor = "original" | "black" | "blue";
export type Rotation = 0 | 90 | 180 | 270;

const INK_RGB: Record<
  Exclude<InkColor, "original">,
  [number, number, number]
> = {
  black: [24, 24, 24],
  blue: [28, 58, 160],
};

export interface SignatureSource {
  width: number;
  height: number;
  pixels: Uint8ClampedArray;
  background: Uint8ClampedArray;
  darkness: Float32Array;
  suggestedSensitivity: number;
}

export interface RenderOptions {
  sensitivity?: number;
  ink?: InkColor;
  erased?: Uint8Array | null;
}

export interface SignatureFrame {
  imageData: ImageData;
  bounds: { minX: number; minY: number; maxX: number; maxY: number } | null;
}

export function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error("image load failed"));
    img.src = src;
  });
}

export function readFileAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

const luminance = (r: number, g: number, b: number) =>
  0.299 * r + 0.587 * g + 0.114 * b;

const clamp = (v: number, lo: number, hi: number) =>
  v < lo ? lo : v > hi ? hi : v;

function estimateBackground(
  px: Uint8ClampedArray,
  w: number,
  h: number,
): Uint8ClampedArray {
  const cell = Math.max(MIN_CELL, Math.round(Math.max(w, h) / GRID_DIVISIONS));
  const gw = Math.ceil(w / cell);
  const gh = Math.ceil(h / cell);
  const n = gw * gh;
  const cr = new Float32Array(n);
  const cg = new Float32Array(n);
  const cb = new Float32Array(n);
  const hist = new Uint32Array(256);

  for (let gy = 0; gy < gh; gy++) {
    const y0 = gy * cell;
    const y1 = Math.min(h, y0 + cell);
    for (let gx = 0; gx < gw; gx++) {
      const x0 = gx * cell;
      const x1 = Math.min(w, x0 + cell);
      hist.fill(0);
      for (let y = y0; y < y1; y++) {
        for (let x = x0; x < x1; x++) {
          const i = (y * w + x) * 4;
          hist[Math.round(luminance(px[i], px[i + 1], px[i + 2]))]++;
        }
      }
      const target = Math.max(1, ((x1 - x0) * (y1 - y0)) / 10);
      let cutoff = 255;
      for (let l = 255, acc = 0; l >= 0; l--) {
        acc += hist[l];
        if (acc >= target) {
          cutoff = l;
          break;
        }
      }
      let sr = 0;
      let sg = 0;
      let sb = 0;
      let count = 0;
      for (let y = y0; y < y1; y++) {
        for (let x = x0; x < x1; x++) {
          const i = (y * w + x) * 4;
          if (Math.round(luminance(px[i], px[i + 1], px[i + 2])) >= cutoff) {
            sr += px[i];
            sg += px[i + 1];
            sb += px[i + 2];
            count++;
          }
        }
      }
      const c = gy * gw + gx;
      cr[c] = sr / count;
      cg[c] = sg / count;
      cb[c] = sb / count;
    }
  }
  const mr = new Float32Array(n);
  const mg = new Float32Array(n);
  const mb = new Float32Array(n);
  const neigh: number[] = [];
  for (let gy = 0; gy < gh; gy++) {
    for (let gx = 0; gx < gw; gx++) {
      neigh.length = 0;
      for (let dy = -1; dy <= 1; dy++) {
        const yy = gy + dy;
        if (yy < 0 || yy >= gh) continue;
        for (let dx = -1; dx <= 1; dx++) {
          const xx = gx + dx;
          if (xx < 0 || xx >= gw) continue;
          neigh.push(yy * gw + xx);
        }
      }
      neigh.sort(
        (a, b) =>
          luminance(cr[a], cg[a], cb[a]) - luminance(cr[b], cg[b], cb[b]),
      );
      const pick = neigh[neigh.length >> 1];
      const c = gy * gw + gx;
      mr[c] = cr[pick];
      mg[c] = cg[pick];
      mb[c] = cb[pick];
    }
  }
  const blur = (src: Float32Array) => {
    const out = new Float32Array(n);
    for (let gy = 0; gy < gh; gy++) {
      for (let gx = 0; gx < gw; gx++) {
        let s = 0;
        let k = 0;
        for (let dy = -1; dy <= 1; dy++) {
          const yy = gy + dy;
          if (yy < 0 || yy >= gh) continue;
          for (let dx = -1; dx <= 1; dx++) {
            const xx = gx + dx;
            if (xx < 0 || xx >= gw) continue;
            s += src[yy * gw + xx];
            k++;
          }
        }
        out[gy * gw + gx] = s / k;
      }
    }
    return out;
  };
  const br = blur(mr);
  const bg = blur(mg);
  const bb = blur(mb);

  const out = new Uint8ClampedArray(w * h * 3);
  for (let y = 0; y < h; y++) {
    const fy = clamp((y + 0.5) / cell - 0.5, 0, gh - 1);
    const y0 = Math.floor(fy);
    const y1 = Math.min(gh - 1, y0 + 1);
    const ty = fy - y0;
    for (let x = 0; x < w; x++) {
      const fx = clamp((x + 0.5) / cell - 0.5, 0, gw - 1);
      const x0 = Math.floor(fx);
      const x1 = Math.min(gw - 1, x0 + 1);
      const tx = fx - x0;
      const a = y0 * gw + x0;
      const b = y0 * gw + x1;
      const c = y1 * gw + x0;
      const d = y1 * gw + x1;
      const wa = (1 - tx) * (1 - ty);
      const wb = tx * (1 - ty);
      const wc = (1 - tx) * ty;
      const wd = tx * ty;
      const o = (y * w + x) * 3;
      out[o] = br[a] * wa + br[b] * wb + br[c] * wc + br[d] * wd;
      out[o + 1] = bg[a] * wa + bg[b] * wb + bg[c] * wc + bg[d] * wd;
      out[o + 2] = bb[a] * wa + bb[b] * wb + bb[c] * wc + bb[d] * wd;
    }
  }
  return out;
}

function suggestSensitivity(darkness: Float32Array): number {
  const hist = new Uint32Array(256);
  for (let i = 0; i < darkness.length; i++)
    hist[Math.min(255, Math.round(darkness[i]))]++;
  const total = darkness.length;
  let sumAll = 0;
  for (let l = 0; l < 256; l++) sumAll += l * hist[l];
  let sumB = 0;
  let wB = 0;
  let best = -1;
  let threshold = DEFAULT_SENSITIVITY + FEATHER / 2;
  for (let l = 0; l < 256; l++) {
    wB += hist[l];
    if (wB === 0) continue;
    const wF = total - wB;
    if (wF === 0) break;
    sumB += l * hist[l];
    const mB = sumB / wB;
    const mF = (sumAll - sumB) / wF;
    const between = wB * wF * (mB - mF) * (mB - mF);
    if (between > best) {
      best = between;
      threshold = l;
    }
  }
  return Math.round(clamp(threshold - FEATHER / 2, 15, 90));
}

export function prepareSignature(
  img: HTMLImageElement,
  rotation: Rotation = 0,
): SignatureSource {
  const scale = Math.min(1, MAX_SIDE / Math.max(img.width, img.height));
  const sw = Math.max(1, Math.round(img.width * scale));
  const sh = Math.max(1, Math.round(img.height * scale));
  const sideways = rotation === 90 || rotation === 270;
  const width = sideways ? sh : sw;
  const height = sideways ? sw : sh;

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d", { willReadFrequently: true })!;
  ctx.fillStyle = "#fff";
  ctx.fillRect(0, 0, width, height);
  ctx.translate(width / 2, height / 2);
  ctx.rotate((rotation * Math.PI) / 180);
  ctx.drawImage(img, -sw / 2, -sh / 2, sw, sh);

  const pixels = ctx.getImageData(0, 0, width, height).data;
  const background = estimateBackground(pixels, width, height);
  const darkness = new Float32Array(width * height);
  for (let p = 0, i = 0, o = 0; p < darkness.length; p++, i += 4, o += 3) {
    const d =
      luminance(background[o], background[o + 1], background[o + 2]) -
      luminance(pixels[i], pixels[i + 1], pixels[i + 2]);
    darkness[p] = d > 0 ? d : 0;
  }

  return {
    width,
    height,
    pixels,
    background,
    darkness,
    suggestedSensitivity: suggestSensitivity(darkness),
  };
}

export function renderSignatureFrame(
  src: SignatureSource,
  {
    sensitivity = DEFAULT_SENSITIVITY,
    ink = "original",
    erased = null,
  }: RenderOptions = {},
): SignatureFrame {
  const { width: w, height: h, pixels, background, darkness } = src;
  const n = w * h;
  const out = new ImageData(w, h);

  const alpha = new Float32Array(n);
  for (let p = 0; p < n; p++)
    alpha[p] =
      erased && erased[p]
        ? 0
        : clamp((darkness[p] - sensitivity) / FEATHER, 0, 1);
  const labels = new Int32Array(n);
  const areas: number[] = [0];
  const stack = new Int32Array(n);
  for (let start = 0; start < n; start++) {
    if (alpha[start] === 0 || labels[start] !== 0) continue;
    const label = areas.length;
    let top = 0;
    let area = 0;
    stack[top++] = start;
    labels[start] = label;
    while (top > 0) {
      const p = stack[--top];
      area++;
      const x = p % w;
      const y = (p - x) / w;
      for (let dy = -1; dy <= 1; dy++) {
        const yy = y + dy;
        if (yy < 0 || yy >= h) continue;
        for (let dx = -1; dx <= 1; dx++) {
          const xx = x + dx;
          if (xx < 0 || xx >= w) continue;
          const q = yy * w + xx;
          if (alpha[q] > 0 && labels[q] === 0) {
            labels[q] = label;
            stack[top++] = q;
          }
        }
      }
    }
    areas.push(area);
  }
  if (areas.length === 1) return { imageData: out, bounds: null };
  let largest = 0;
  for (let l = 1; l < areas.length; l++)
    if (areas[l] > largest) largest = areas[l];
  const minArea = Math.min(largest, clamp(Math.round(largest * 0.002), 4, 24));
  const hist = new Uint32Array(256);
  let kept = 0;
  for (let p = 0; p < n; p++) {
    if (labels[p] === 0 || areas[labels[p]] < minArea) continue;
    hist[Math.min(255, Math.round(darkness[p]))]++;
    kept++;
  }
  let inkDarkness = sensitivity + FEATHER;
  for (let l = 255, acc = 0; l >= 0; l--) {
    acc += hist[l];
    if (acc >= kept * 0.1) {
      inkDarkness = Math.max(inkDarkness, l);
      break;
    }
  }

  const od = out.data;
  const fixed = ink === "original" ? null : INK_RGB[ink];
  let minX = w;
  let minY = h;
  let maxX = -1;
  let maxY = -1;
  for (let p = 0, i = 0, o = 0; p < n; p++, i += 4, o += 3) {
    const label = labels[p];
    if (label === 0 || areas[label] < minArea) continue;
    if (fixed) {
      od[i] = fixed[0];
      od[i + 1] = fixed[1];
      od[i + 2] = fixed[2];
    } else {
      const m = clamp(darkness[p] / inkDarkness, 0.15, 1);
      for (let c = 0; c < 3; c++)
        od[i + c] = (pixels[i + c] - (1 - m) * background[o + c]) / m;
    }
    od[i + 3] = Math.round(alpha[p] * 255);
    const x = p % w;
    const y = (p - x) / w;
    if (x < minX) minX = x;
    if (y < minY) minY = y;
    if (x > maxX) maxX = x;
    if (y > maxY) maxY = y;
  }
  return {
    imageData: out,
    bounds: maxX < 0 ? null : { minX, minY, maxX, maxY },
  };
}

export function renderSignature(
  src: SignatureSource,
  options: RenderOptions = {},
): string | null {
  const { imageData: out, bounds } = renderSignatureFrame(src, options);
  if (!bounds) return null;
  const { minX, minY, maxX, maxY } = bounds;
  const { width: w, height: h } = src;
  const cropX = Math.max(0, minX - PADDING);
  const cropY = Math.max(0, minY - PADDING);
  const cropW = Math.min(w, maxX + PADDING + 1) - cropX;
  const cropH = Math.min(h, maxY + PADDING + 1) - cropY;
  const canvas = document.createElement("canvas");
  canvas.width = cropW;
  canvas.height = cropH;
  canvas.getContext("2d")!.putImageData(out, -cropX, -cropY);
  return canvas.toDataURL("image/png");
}

export function removeSignatureBackground(
  img: HTMLImageElement,
  sensitivity = DEFAULT_SENSITIVITY,
  ink: InkColor = "original",
): string | null {
  return renderSignature(prepareSignature(img), { sensitivity, ink });
}
